/**
 * Màu nhận dạng của sprint — người dùng chọn, hoặc suy ra từ `id`.
 *
 * ## Vì sao hex thật, không phải lớp Tailwind
 *
 * Màu này đi vào `style` (viền khung, chấm tròn cạnh bộ chọn) chứ không vào
 * `className`: Tailwind quét **mã nguồn** để biết sinh lớp nào, nên một lớp
 * ghép lúc chạy từ giá trị trong CSDL sẽ không tồn tại — khung hiện đúng chỗ,
 * đúng kích thước, và **không màu**.
 *
 * ## Vì sao vẫn có màu khi người dùng chưa chọn
 *
 * Sprint tạo trước tính năng này — và sprint của người không buồn chọn màu —
 * vẫn cần phân biệt được với nhau. Băm từ `id` cho ra màu **ổn định**: cùng một
 * sprint ra cùng một màu ở board, ở bộ chọn và ở sơ đồ, dù ba chỗ đó tải ba
 * danh sách khác nhau. Lấy theo vị trí trong danh sách thì Sprint 3 xanh ở màn
 * hình này và cam ở màn hình kia.
 */

/**
 * Bảng màu để chọn. Tám ô, cùng góc màu với `--color-tag-*`.
 *
 * Hex chứ không phải `var(--color-tag-1)`: giá trị này **được lưu xuống CSDL**,
 * và một biến CSS đã lưu sẽ đổi nghĩa vào ngày ai đó chỉnh lại theme — sprint
 * cũ tự đổi màu mà không ai đụng vào nó.
 */
export const SPRINT_PALETTE = [
  '#3b6fd4',
  '#8b5cf6',
  '#d946a5',
  '#e2683c',
  '#c99a1f',
  '#3f9c5a',
  '#1fa6a0',
  '#2f8fd0',
] as const;

/** FNV-1a — cùng thuật toán với `tagClassOf`, để hai chỗ không lệch nhau. */
function hashOf(id: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < id.length; index += 1) {
    hash = Math.imul(hash ^ id.charCodeAt(index), 16_777_619);
  }
  return hash >>> 0;
}

/** Màu người dùng đặt, hoặc màu suy ra từ `id` khi chưa đặt. */
export function sprintColor(sprint: {
  readonly id: string;
  readonly color: string | null;
}): string {
  return sprint.color ?? SPRINT_PALETTE[hashOf(sprint.id) % SPRINT_PALETTE.length]!;
}

/**
 * Nền rất nhạt của chính màu đó.
 *
 * `color-mix` thay vì tự tính rgba: nó giữ đúng màu người dùng chọn và tự lo
 * phần trộn, kể cả khi giá trị là hex hay `oklch()`.
 */
export function sprintTint(color: string, percent = 4): string {
  return `color-mix(in oklch, ${color} ${percent}%, transparent)`;
}
